import { useEffect, useState } from "react"
import { Button, Dropdown, Input, Modal, Tag, Tooltip, message } from "antd"
import { MoreOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons"
import apiClient from "../../../Utils/apiClient"
import TableBox from "../../../components/TableBox/TableBox"
import PanelLayout from "../../../Layout/PanelLayout"
import { getStoredPageSize } from "../../../Utils/TableUtils/TableUtils"
import { useNavigate } from "react-router-dom"
import "../AppManagement.scss"
import "../../Operations/Operations.scss"

const AppBundles = () => {
  const navigate = useNavigate()
  const page = "App Bundles"
  const [loading, setLoading] = useState(false)
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
    total: 0,
  })
  const [data, setData] = useState([])
  const [searchInput, setSearchInput] = useState("")
  const [filters, setFilters] = useState({
    name: "",
    PackageId: "",
  })

  const getData = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize,
    customFilters = filters
  ) => {
    setLoading(true)
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        ...customFilters,
      }
      const response = await apiClient("group-apps/get-all", "POST", params)
      if (response?.success) {
        const { data: resultData, pageData } = response.result
        setData(resultData || [])
        setPaginationData(pageData || paginationData)
      }
    } catch (err) {
      console.error("Error fetching group apps:", err)
      message.error("Failed to load app bundles")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getData(1, paginationData.pageSize)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!searchInput && !filters.name) return
      const next = { name: searchInput.trim(), PackageId: "" }
      setFilters(next)
      setPaginationData((prev) => ({ ...prev, page: 1 }))
      getData(1, paginationData.pageSize, next)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const handleDelete = (record) => {
    Modal.confirm({
      title: `Delete bundle “${record.name}”?`,
      content: "Devices using this bundle will keep installed apps, but the bundle will be removed.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          const remove = await apiClient(`group-apps/remove/${record._id}`, "DELETE")
          if (remove.success) {
            message.success(remove.message)
            getData(paginationData.page, paginationData.pageSize)
          }
        } catch (err) {
          console.error("Delete error:", err)
          message.error("Failed to delete")
        }
      },
    })
  }

  const columns = [
    {
      title: "Bundle",
      dataIndex: "name",
      key: "name",
      render: (name) => <span className="app-mgmt__name">{name || "—"}</span>,
    },
    {
      title: "Applications",
      dataIndex: "applicationsList",
      key: "applicationsList",
      render: (apps) => {
        if (!apps || !apps.length) return <span className="app-mgmt__muted">None</span>
        const names = apps.map((app) => app.PackageId).filter(Boolean)
        const extra = names.length > 4 ? names.length - 4 : 0
        return (
          <Tooltip title={names.join(", ")}>
            <div className="app-mgmt__chips">
              {names.slice(0, 4).map((name) => (
                <Tag key={name}>{name}</Tag>
              ))}
              {extra ? <Tag>+{extra}</Tag> : null}
            </div>
          </Tooltip>
        )
      },
    },
    {
      title: "Apps",
      key: "count",
      width: 80,
      render: (_, record) => record.applicationsList?.length || 0,
    },
    {
      title: "",
      key: "actions",
      width: 56,
      align: "right",
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          menu={{
            items: [
              {
                key: "edit",
                label: "Edit",
                onClick: () => navigate(`/edit-app-bundles/${record._id}`),
              },
              {
                key: "delete",
                label: "Delete",
                danger: true,
                onClick: () => handleDelete(record),
              },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} aria-label="Bundle actions" />
        </Dropdown>
      ),
    },
  ]

  const appTotal = data.reduce(
    (sum, row) => sum + (row.applicationsList?.length || 0),
    0
  )
  const kpis = [
    {
      key: "bundles",
      label: "Bundles",
      value: paginationData.total || data.length,
      tone: "#2563eb",
      soft: "rgba(59, 130, 246, 0.14)",
    },
    {
      key: "apps",
      label: "Apps in view",
      value: appTotal,
      tone: "#0891b2",
      soft: "rgba(56, 189, 248, 0.14)",
    },
    {
      key: "empty",
      label: "Empty bundles",
      value: data.filter((row) => !(row.applicationsList || []).length).length,
      tone: "#d97706",
      soft: "rgba(245, 158, 11, 0.16)",
    },
  ]

  return (
    <PanelLayout
      title="App bundles"
      subtitle="Package apps together and deploy them as a single assignment."
      actions={
        <>
          <Input
            className="app-mgmt__search"
            prefix={<SearchOutlined />}
            allowClear
            placeholder="Search bundle name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/add-app-bundles")}
          >
            Add bundle
          </Button>
        </>
      }
    >
      <div className="ops-kpis">
        {kpis.map((card) => (
          <div
            key={card.key}
            className="ops-kpi ops-kpi--static"
            style={{ "--act-tone": card.tone, "--act-soft": card.soft }}
          >
            <div>
              <span className="ops-kpi-label">{card.label}</span>
              <strong style={{ fontSize: 17 }}>{card.value}</strong>
            </div>
          </div>
        ))}
      </div>
      <TableBox
        className="dashboard-inventory-table"
        page={page}
        title={null}
        columns={columns}
        data={data}
        loading={loading}
        pagination={{
          current: paginationData.page,
          pageSize: paginationData.pageSize,
          total: paginationData.total,
          showSizeChanger: true,
        }}
        onChange={(pager) => {
          setPaginationData((prev) => ({
            ...prev,
            page: pager.current,
            pageSize: pager.pageSize,
          }))
          getData(pager.current, pager.pageSize, filters)
        }}
      />
    </PanelLayout>
  )
}

export default AppBundles
